// Part SKUs --- a part's SKU starts with its type's prefix, so parts never look like the SKUs HHL sells on Shopify:
// MAT- a material, PAK- packaging (any kind), SUB- a sub-assembly, SVC- a service; the rest is readable text
// (MAT-GCU2-SHELL-BK, PAK-HAPTIC-BAG). Mitch's rule (2026-10-31): required on every part made or given a new SKU in
// HHL Ops, and changing a part's type swaps it (MAT-GCU2-BOX → PAK-GCU2-BOX). Migration 042 put it on the parts there
// were; a SKU with no type prefix at all (test and demo data) is left alone until someone changes it. Sell items keep
// Shopify's SKUs. The importers match a SKU with or without its prefix (`skuStem`), so data files written before the
// rule still find their parts.

export const PART_PREFIXES = { material: "MAT-", packaging: "PAK-", subassembly: "SUB-", service: "SVC-" } as const;
const ANY_PREFIX = /^(MAT|PAK|SUB|SVC)-/i;
const NOUNS: Record<string, string> = { material: "material", packaging: "packaging part", subassembly: "sub-assembly", service: "service" };

type PartType = { kind: string; part_type?: string | null };

/** The prefix a part's SKU starts with: packaging (whatever its kind) PAK-, else by kind; "" for a sell item. */
export function skuPrefix(p: PartType): string {
  if (p.kind === "sell") return "";
  if (p.part_type === "packaging") return PART_PREFIXES.packaging;
  return PART_PREFIXES[p.kind as keyof typeof PART_PREFIXES] ?? "";
}

/** The SKU without its type prefix: "MAT-GCU2-SHELL-BK" → "GCU2-SHELL-BK" (one with none is unchanged). */
export function skuStem(sku: string): string {
  return sku.replace(ANY_PREFIX, "");
}

/** Whether the SKU starts with a type prefix at all. */
export function hasSkuPrefix(sku: string): boolean {
  return ANY_PREFIX.test(sku);
}

/** The SKU with this type's prefix in place of the one it has ("MAT-GCU2-BOX" as packaging → "PAK-GCU2-BOX"). A SKU
 * with no type prefix, or a sell item's, is left alone. */
export function swapSkuPrefix(sku: string, p: PartType): string {
  const prefix = skuPrefix(p);
  return prefix && hasSkuPrefix(sku) ? prefix + skuStem(sku) : sku;
}

/** Why this SKU won't do for a part of this type, or null: it must start with the type's prefix and say something after it. */
export function skuProblem(sku: string, p: PartType): string | null {
  const prefix = skuPrefix(p);
  if (!prefix) return null;
  const noun = NOUNS[p.part_type === "packaging" ? "packaging" : p.kind] ?? "part";
  const example = `${prefix}${skuStem(sku) || "GCU2-SHELL-BK"}`;
  if (!sku.toUpperCase().startsWith(prefix)) return `A ${noun}'s SKU starts with ${prefix} (${example})`;
  if (!skuStem(sku).trim()) return `A ${noun}'s SKU needs something after ${prefix} (${example})`;
  return null;
}

/** A SKU written before the rule, as migration 042 renames it: the type's prefix in front, an existing one replaced, and
 * the type's own word dropped where the old SKU repeated it (GCU2-SVC-R5 → SVC-GCU2-R5). */
export function prefixedSku(sku: string, p: PartType): string {
  const prefix = skuPrefix(p);
  if (!prefix || !sku) return sku;
  const word = prefix.slice(0, -1);
  return prefix + skuStem(sku).replace(new RegExp(`-${word}(-|$)`, "i"), "$1");
}
